/**
 * Route table.
 *
 * The app uses a HASH router, so these become `#/lockerroom`, `#/agent/sr` and
 * so on — identical to the prototype's URLs. Two reasons that is the right
 * choice here and not a legacy artefact:
 *
 *   - Deep links shared from the prototype keep working.
 *   - A static build needs no server rewrite rule; it can be opened from any
 *     path, a file share, or a CDN folder without a catch-all route.
 *
 * Query strings inside the hash (`#/agent/sr/dashboard?partner=visma`) are read
 * with `useSearchParams`, which the hash router supports.
 */

export const PATHS = {
  arena: '/arena',
  game: '/game',
  gym: '/gym',
  frontOffice: '/front-office',
  lockerroom: '/lockerroom',
  account: '/account',
  settings: '/settings',

  agentDataQa: '/agent/dataqa',
  agentDqsMeasurement: '/agent/dqsm',
  agentDqsRecommendation: '/agent/dqsr',

  agentMmm: '/agent/mmm',
  agentMmmOutput: '/agent/mmm/output',

  agentProi: '/agent/proi',

  agentSr: '/agent/sr',
  agentSrConfigChoice: '/agent/sr/config-choice',
  agentSrConfig: '/agent/sr/config',
  agentSrWizard: '/agent/sr/wizard',
  agentSrDashboard: '/agent/sr/dashboard',
  agentSrPortfolio: '/agent/sr/portfolio',
  agentSrAdmin: '/agent/sr/admin',

  agentPricing: '/agent/pricing',

  adminUsage: '/admin/usage',

  reportDqs: '/report/dqs',
  reportActivation: '/report/activation',
  reportActivationCreate: '/report/activation/create',
  reportActivationBuild: '/report/activation/build',
  reportActivationView: '/report/activation/view',
} as const;

export type AppPath = (typeof PATHS)[keyof typeof PATHS];

/** Builds a path with a query string, dropping empty values. */
export function withQuery(
  path: string,
  params: Readonly<Record<string, string | number | undefined>>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Lockerroom tab deep links, used by the rail's Views group. */
export const lockerroomTab = (tab: string): string => withQuery(PATHS.lockerroom, { tab });

/** Settings section deep links, e.g. linking straight to Human Layer · My Profile. */
export const settingsSection = (section: string): string => withQuery(PATHS.settings, { section });

/**
 * The delegate route pattern (US-A11). A top-level route OUTSIDE the app shell —
 * a right holder opening the link is not an arena user and should see only the
 * scoped form, never the rail or top bar.
 */
export const DELEGATE_ROUTE = '/delegate/:token';

/**
 * Absolute, shareable link to a delegation. Built against the current origin so
 * it works from wherever the app is served; the hash keeps it static-host safe.
 */
export function delegateUrl(token: string): string {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}#/delegate/${encodeURIComponent(token)}`;
}
